import { useState, useEffect } from 'react';

export interface CatalogTranslation {
  title: string;
  description: string;
}

export interface CatalogImage {
  originalFilename: string;
  newFilename: string;
  ranking?: number; // Higher number = higher priority
  translations: {
    english: CatalogTranslation;
    german: CatalogTranslation;
    spanish: CatalogTranslation;
    french: CatalogTranslation;
  };
}

export interface CatalogData {
  metadata: {
    totalImages: number;
    languages: string[];
  };
  images: CatalogImage[];
}

export interface ProcessedImage {
  id: string;
  src: string;
  title: string;
  description: string;
  originalFilename: string;
  ranking?: number;
}

const useCatalogData = (language: string = 'german') => {
  const [catalogData, setCatalogData] = useState<CatalogData | null>(null);
  const [images, setImages] = useState<ProcessedImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadCatalogData = async () => {
      try {
        setLoading(true);
        const response = await fetch('/images/gallery/default/catalog.json');
        if (!response.ok) {
          throw new Error('Failed to load catalog data');
        }
        const data: CatalogData = await response.json();
        setCatalogData(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    loadCatalogData();
  }, []);

  useEffect(() => {
    if (!catalogData) return;

    const languageMap: { [key: string]: keyof CatalogImage['translations'] } = {
      'de': 'german',
      'en': 'english',
      'es': 'spanish',
      'fr': 'french',
      'german': 'german',
      'english': 'english',
      'spanish': 'spanish',
      'french': 'french'
    };

    const selectedLanguage = languageMap[language] || 'german';

    const processedImages = catalogData.images.map((image) => ({
      id: `${image.newFilename}-${selectedLanguage}`,
      src: `/images/gallery/default/${image.newFilename}`,
      title: image.translations[selectedLanguage]?.title || image.translations.english.title,
      description: image.translations[selectedLanguage]?.description || image.translations.english.description,
      originalFilename: image.originalFilename,
      ranking: image.ranking
    }));

    // Special handling: Ensure yggdrasils-lantern is always in top 3, with randomized order
    const yggdrasilsImage = processedImages.find(img =>
      img.src.includes('yggdrasils-lantern-at-fjord-dusk')
    );

    // Get all images except yggdrasils
    const otherImages = processedImages.filter(img =>
      !img.src.includes('yggdrasils-lantern-at-fjord-dusk')
    );

    // Sort other images by ranking (highest first), then randomly for unranked images
    const sortedOtherImages = otherImages.sort((a, b) => {
      // If both have ranking, sort by ranking (higher first)
      if (a.ranking !== undefined && b.ranking !== undefined) {
        return b.ranking - a.ranking;
      }
      // If only one has ranking, ranked image goes first
      if (a.ranking !== undefined && b.ranking === undefined) {
        return -1;
      }
      if (a.ranking === undefined && b.ranking !== undefined) {
        return 1;
      }
      // If neither has ranking, sort randomly
      return Math.random() - 0.5;
    });

    // Get 2 random images from the sorted list (excluding yggdrasils)
    const topTwo = sortedOtherImages.slice(0, 2);

    // Create top 3 array with yggdrasils and 2 others
    const topThree = yggdrasilsImage ? [yggdrasilsImage, ...topTwo] : topTwo;

    // Randomize the order of top 3
    const shuffledTopThree = topThree.sort(() => Math.random() - 0.5);

    // Combine shuffled top 3 with remaining images
    const remainingImages = sortedOtherImages.slice(2);
    const finalImages = [...shuffledTopThree, ...remainingImages];

    setImages(finalImages);
  }, [catalogData, language]);

  return {
    images,
    catalogData,
    loading,
    error,
    totalImages: catalogData?.metadata.totalImages || 0
  };
};

export default useCatalogData;